// hooks/useAIEventGeneration.ts

import { useState, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  useGenerateEventMutation,
  useGenerateTemplateStructureMutation,
  useEnhanceEventDescriptionMutation,
} from '@/store/features/ai-events/aiEventsAPI';
import {
  setGeneratedEvent,
  clearGeneratedEvent,
  setGenerationProgress,
  setGenerationError,
} from '@/store/features/ai-events/aiEventsSlice';
import {
  GenerateEventRequest,
  EventIndustry,
  EventType,
  EventTemplateType,
} from '@/store/features/ai-events/aiEventsTypes';

export const useAIEventGeneration = () => {
  const dispatch = useAppDispatch();
  const { generatedEvent, isGenerating, generationError, savedEventId } = useAppSelector(
    (state) => state.aiEvent
  );

  const [generateEvent] = useGenerateEventMutation();
  const [generateTemplate] = useGenerateTemplateStructureMutation();
  const [enhanceDescription] = useEnhanceEventDescriptionMutation();

  // Generate event from prompt
  const generate = useCallback(
    async (request: GenerateEventRequest) => {
      try {
        dispatch(setGenerationProgress(10));

        const result = await generateEvent(request).unwrap();

        dispatch(setGeneratedEvent(result.event));
        dispatch(setGenerationProgress(100));

        return result;
      } catch (error: any) {
        dispatch(setGenerationError(error?.data?.error || 'Generation failed'));
        throw error;
      }
    },
    [generateEvent, dispatch]
  );

  // Quick generation with defaults
  const quickGenerate = useCallback(
    async (prompt: string, industry: EventIndustry) => {
      return generate({
        prompt,
        industry,
      });
    },
    [generate]
  );

  // Generate with template
  const generateWithTemplate = useCallback(
    async (industry: EventIndustry, eventId?: string) => {
      try {
        const result = await generateTemplate({
          industry,
          template_type: 'event' as EventTemplateType,
          event_id: eventId,
        }).unwrap();

        return result;
      } catch (error: any) {
        dispatch(setGenerationError(error?.data?.error || 'Template generation failed'));
        throw error;
      }
    },
    [generateTemplate, dispatch]
  );

  // Enhance existing description
  const enhance = useCallback(
    async (
      eventId: string,
      currentDescription: string,
      tone?: 'professional' | 'casual' | 'exciting' | 'formal'
    ) => {
      try {
        const result = await enhanceDescription({
          event_id: eventId,
          current_description: currentDescription,
          tone,
        }).unwrap();

        return result.enhanced_description;
      } catch (error: any) {
        dispatch(setGenerationError(error?.data?.error || 'Enhancement failed'));
        throw error;
      }
    },
    [enhanceDescription, dispatch]
  );

  // Clear generated event
  const clear = useCallback(() => {
    dispatch(clearGeneratedEvent());
  }, [dispatch]);

  return {
    // State
    generatedEvent,
    isGenerating,
    generationError,
    savedEventId,

    // Methods
    generate,
    quickGenerate,
    generateWithTemplate,
    enhance,
    clear,
  };
};
